import React, { useState, useEffect, useCallback } from 'react';
import TabPanel from 'devextreme-react/tab-panel';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { Button } from 'devextreme-react/button';
import { useNavigate } from 'react-router-dom';
import {getAPIReportData, adminGetSettings, adminUpdateSettings} from '../../api/api'
import { SelectBox } from 'devextreme-react/select-box';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { ESGQuantitativeMetrics, ESGQuantitativeMetricsOvG, ESGQualitativeMetrics,
  ESGEmergingTopics, ESGEmissions, ESGReportingStrategy, ESGTCFDMaturity, ESGGoals,
  ESGRsg, ESGReportData} from '../../components';
import './esg.scss';

export default function ESG() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [yearsList, setYearsList] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [settingsESGYears, setSettingsESGYears] = useState([]);
  const [reportTabs, setReportTabs] = useState();

  const onDataSuccess = useCallback((data) => {
    let rt = [
      {
        title: 'Quantitative Metrics',
        icon: "fas fa-calculator",
        children: (<ESGQuantitativeMetrics data={data} year={currentYear}/>)
      },
      {
        title: 'Quantitative Metrics(Oil vs Gas)',
        icon: "fas fa-calculator",
        children: (<ESGQuantitativeMetricsOvG data={data} year={currentYear}/>)
      },
      {
        title: 'Qualitative Metrics',
        icon: "fas fa-hand-holding-water",
        children: (<ESGQualitativeMetrics data={data} year={currentYear}/>)
      },
      {
        title: 'Reporting Strategies',
        icon: "fas fa-file-invoice",
        children: (<ESGReportingStrategy data={data} year={currentYear}/>)
      },
      {
        title: 'TCFD Maturity',
        icon: "fas fa-leaf",
        children: (<ESGTCFDMaturity data={data} year={currentYear}/>)
      },
      {
        title: 'RSG and Other Initiatives',
        icon: "fas fa-award",
        children: (<ESGRsg data={data} year={currentYear}/>)
      },
      {
        title: 'Goals and Targets',
        icon: "fas fa-bullseye",
        children: (<ESGGoals data={data} year={currentYear}/>)
      },
      {
        title: 'Emerging Topics',
        icon: "fas fa-industry",
        children: (<ESGEmergingTopics data={data} year={currentYear}/>)
      },
      {
        title: 'Emissions',
        icon: "fab fa-gripfire",
        children: (<ESGEmissions data={data} year={currentYear}/>)
      },
      {
        title: 'Report Data',
        icon: "fas fa-database",
        children: (<ESGReportData data={data} year={currentYear}/>)
      }
    ]
    setReportTabs(rt)
    setLoading(false);
  }, [setReportTabs, currentYear])

  const onSettingsSuccess = useCallback((data) => {
    for (var i=0; i<data.length; i++) {
      if (data[i]["setting"] === "esg_published_years") {
        if (user.signInUserSession) {
          let groups = user.signInUserSession.idToken.payload['cognito:groups']
          if (groups && (!groups.includes('admin'))) {
            if (data[i]["value"].length) {
              setYearsList([...data[i]["value"]]);
            } else {
              notify("No ESG reports are available at this time. Please try again later.", 'warning', 5000);
              navigate("/home");
            }
          } else {
            const cy = new Date().getFullYear();
            var yl = []
            for (var j=2024; j<=cy; j++) {
              yl.push(j.toString())
            }
            setYearsList(yl);
          }
        }

        var max = null;
        if (data[i]["value"].length) {
          max = data[i]["value"].reduce((max, c) => c > max ? c : max)
          setSettingsESGYears(data[i]["value"]);
        } else {
          const cy = new Date().getFullYear();
          max = (cy-1).toString();
          setSettingsESGYears([]);
        }
        if (!currentYear) {
          setCurrentYear(max);
        }
      }
    }
  }, [setSettingsESGYears, currentYear, setCurrentYear, setYearsList, user, navigate])

  const onSettingsUpdateSuccess = useCallback((data) => {
    for (var i=0; i<data.length; i++) {
      if (data[i]["setting"] === "esg_published_years") {
        setSettingsESGYears(data[i]["value"]);
        notify("Report published succesfully.", 'success', 5000);
        if (data[i]["value"].includes(currentYear)) {
          setLoading(false)
        } else {
          const max = data[i]["value"].reduce((max, c) => c > max ? c : max)
          setCurrentYear(max);
        }
      }
    }
  }, [setSettingsESGYears, currentYear, setCurrentYear])

  const onError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      setLoading(false);
      console.log(error);
    }
  }, [refreshSession]);

  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i].includes("esgro")) {
            has_perm=true;
          }
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
          }
        }
      }

      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }
      adminGetSettings(user, onSettingsSuccess, onError);
    }
  }, [user, navigate, onSettingsSuccess, onError]);

  function TabPanelItem({ data }) {
    return <div className="tabpanel-item px-3">{data.children}</div>;
  }

  const onYearSelected = useCallback((e) => {
    setCurrentYear(e.value);
  }, [setCurrentYear])

  const onPublishYear = useCallback((e) => {
    var yl = [...settingsESGYears, currentYear]
    yl.sort();
    setLoading(true);
    adminUpdateSettings(user, {esg_published_years: yl}, onSettingsUpdateSuccess, onError)

  }, [currentYear, settingsESGYears, onSettingsUpdateSuccess, onError, user])

  const onUnpublishYear = useCallback((e) => {
    var yl = []
    for (var i=0; i<settingsESGYears.length; i++) {
      if (settingsESGYears[i] !== currentYear) {
        yl.push(settingsESGYears[i])
      }
    }
    yl.sort();
    setLoading(true);
    adminUpdateSettings(user, {esg_published_years: yl}, onSettingsUpdateSuccess, onError)
  }, [currentYear, settingsESGYears, onSettingsUpdateSuccess, onError, user])

  useEffect(() => {
    if (currentYear) {
      setLoading(true);
      getAPIReportData('/reports/esg/'+currentYear, user, {}, onDataSuccess, onError);
    }
  }, [currentYear, user, onDataSuccess, onError]);


  if(!loading) {
  return (
    <React.Fragment>
      <div className='content-block d-flex flex-row justify-content-between'>
      <h2>Environmental, Social & Governance Benchmarking - {currentYear}</h2>
      <div className='d-flex flex-row'>
      {isAdmin && <Button
        text="Company Data"
        stylingMode="outlined"
        type="default"
        onClick={() => {navigate("/esgdata")}}
      />}

      </div>
      </div>
      <hr className="mt-0 mb-5"></hr>
      {yearsList.length &&
        <div className='content-block d-flex flex-row justify-content-between'>
          <div className='d-flex flex-column'>
            {isAdmin && <h5 className="mt-3"> Choose the reporting year</h5>}
            <SelectBox
                value={currentYear}
                dataSource={yearsList}
                showDropDownButton={true}
                activeStateEnabled={true}
                onValueChanged={onYearSelected}
                label={"Reporting year"}
                labelMode="floating"
            >
            </SelectBox>
          </div>
          {isAdmin && currentYear && <Button
            text={settingsESGYears.includes(currentYear)?`Un-publish year ${currentYear}`:`Publish year ${currentYear}`}
            stylingMode="normal"
            type="danger"
            onClick={settingsESGYears.includes(currentYear)?onUnpublishYear:onPublishYear}
          />}
        </div>

      }
      {currentYear && <div className={'content-block'}>

        <div className={'dx-card responsive-paddings'}>
          <TabPanel
            width="100%"
            height={"auto"}
            animationEnabled={false}
            swipeEnabled={true}
            dataSource={reportTabs}
            tabsPosition={'left'}
            stylingMode={'secondary'}
            iconPosition={'left'}
            itemComponent={TabPanelItem}
          />
        </div>
      </div>}
    </React.Fragment>
  )
} else {
  return (
    <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
        <LoadIndicator
            id="large-indicator"
            height={60}
            width={60}
        />
    </div>
  )
}

}
